import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useActivePlayer } from '@/context/ActivePlayerContext';
import { useBackgroundSync } from '@/context/BackgroundSyncContext';
import Checkbox from '@/common/Checkbox';
import ConnectivityIndicator from '@/common/ConnectivityIndicator';
import SyncIndicator from '@/common/SyncIndicator/SyncIndicator';
import Modal from '@/common/Modal';
import Subnavbar from './Subnavbar';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import PlayerContract from '@/components/TourBag/PlayerContract/PlayerContract';
import { ITourContractSettings } from '@/library/ContractSettingsHelper';
import { getUser } from '@/library/UserSessionHelper';
import { getEnvLabel, isMobileResolution, isProductionEnv } from '@/utils';
import ArrowLeftSlickIcon from '@/assets/icons/arrow-left-slick.svg?react';
import InformationIcon from '@/assets/icons/information.svg?react';
import CloseIcon from '@/assets/icons/close-x.svg?react';
import styles from './PlayerNavbar.module.scss';

function PlayerNavbar() {
  const { goToTourBagPage } = useAppNavigation();
  const isOnline = useOnlineStatus();
  const { lastSyncDate } = useBackgroundSync();
  const [showPlayerInfo, setShowPlayerInfo] = useState(false);
  const { player, isMyPlayer, handleMyPlayer } = useActivePlayer();
  const [enableEdit, setEnableEdit] = useState(false);
  const [enableHistory, setEnableHistory] = useState(false);
  const [contractStatus, setContractStatus] = useState('');

  const handleBack = () => {
    goToTourBagPage();
  };

  useEffect(() => {
    const getContractSettings = async () => {
      const user = await getUser();
      const contractSettings = user?.contractSettings as ITourContractSettings;
      if (contractSettings) {
        setEnableEdit(contractSettings.contractEdit);
        setEnableHistory(contractSettings.contractHistory);
      }
      setContractStatus(player?.contract?.status || 'No Contract');
    };
    getContractSettings();
  }, [player]);

  return (
    <>
      <header className={styles.playerNavbar}>
        <div className={styles.titleWrapper}>
          <button className={styles.backButton} onClick={handleBack}>
            <span className={styles.circle}>
              <ArrowLeftSlickIcon />
            </span>
          </button>
          <h1 className={styles.pageTitle}>
            {player && <>{player.fullName}</>}
            <span className={styles.onlineStatus}>
              <ConnectivityIndicator />
              {!isOnline && !isMobileResolution && <div className={styles.lastSync}>Last sync: {dayjs(lastSyncDate).format('MM/DD HH:mm')}</div>}
            </span>
            <SyncIndicator className={styles.syncIndicatorOffset} />
          </h1>
          {!isProductionEnv && player && <div className={styles.envLabel}>{`${getEnvLabel()?.toUpperCase()} ${isMobileResolution ? '' : 'ENV'}`}</div>}
          {isMobileResolution && (
            <>
              <InformationIcon onClick={() => setShowPlayerInfo(true)} />
            </>
          )}
        </div>
        {!isMobileResolution && player && (
          <div className={styles.playerInfo}>
            <div>{player?.defaultTourName}</div>
            <div>ID - {player?.playerId}</div>
            <div className={styles.contract}>
              <span className={styles.contractStatus}>{contractStatus} </span>
              <InformationIcon className={styles.contractIcon} onClick={() => setShowPlayerInfo(true)} />
            </div>

            {isOnline && <Checkbox label="My Player" checked={isMyPlayer} onClick={handleMyPlayer} />}
          </div>
        )}
      </header>
      <Subnavbar />
      {showPlayerInfo && (
        <>
          <Modal
            isOpen={showPlayerInfo}
            onClose={() => setShowPlayerInfo(false)}
            className={styles.modal}
            footer={
              isMobileResolution &&
              isOnline && (
                <div className={styles.myPlayer}>
                  <Checkbox label="My Player" checked={isMyPlayer} onClick={handleMyPlayer} />
                </div>
              )
            }
          >
            <CloseIcon className={styles.closeButton} onClick={() => setShowPlayerInfo(false)} />
            {!isOnline && <div className={styles.lastSyncMobile}>Last sync: {dayjs(lastSyncDate).format('MM/DD HH:mm')}</div>}
            <PlayerContract
              player={player}
              showModal={showPlayerInfo}
              onSave={(contract) => setContractStatus(contract.status)}
              isMyPlayer={isMyPlayer}
              handleMyPlayer={handleMyPlayer}
              enableEdit={enableEdit}
              enableHistory={enableHistory}
            />
          </Modal>
        </>
      )}
    </>
  );
}

export default PlayerNavbar;
